<script setup lang="ts">
import { computed, useAttrs } from 'vue';

defineOptions({ inheritAttrs: false, name: 'EGS_Chip' });
type ChipVariant = 'muted' | 'solid' | 'outline';

const props = withDefaults(
	defineProps<{
		clickable?: boolean;
		disabled?: boolean;
		active?: boolean;
		variant?: ChipVariant | string;
		severity?: string;
		value?: string | number;
		icon?: string;
		rounded?: boolean;
		title?: string;
	}>(),
	{ clickable: false, disabled: false, active: false, variant: 'muted', severity: '', value: '', icon: '', rounded: true, title: '' }
);
const emit = defineEmits<{ click: [event: MouseEvent] }>();
const attrs = useAttrs();
const variants: Record<string, string> = {
	muted: 'bg-[var(--egs-content-hover)] text-color border-surface',
	solid: 'bg-emerald-600 text-white border-emerald-600',
	outline: 'bg-transparent text-color border-surface'
};
const severities: Record<string, string> = {
	primary: 'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200',
	secondary: 'border-surface bg-surface-100 text-surface-700 dark:bg-surface-800 dark:text-surface-200',
	success: 'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200',
	info: 'border-sky-200 bg-sky-100 text-sky-800 dark:border-sky-800 dark:bg-sky-950/60 dark:text-sky-200',
	review: 'border-orange-300 bg-orange-100 text-orange-900 dark:border-orange-700 dark:bg-orange-950/60 dark:text-orange-200',
	warn: 'border-amber-200 bg-amber-100 text-amber-900 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-200',
	warning: 'border-amber-200 bg-amber-100 text-amber-900 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-200',
	danger: 'border-red-200 bg-red-100 text-red-800 dark:border-red-800 dark:bg-red-950/60 dark:text-red-200',
	contrast: 'border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-slate-900'
};
const classes = computed(() => [
	'inline-flex items-center gap-1 whitespace-nowrap border px-2.5 py-1 text-xs font-semibold leading-none',
	props.rounded ? 'rounded-full' : 'rounded-md',
	props.severity ? (severities[props.severity] ?? severities['secondary']) : (variants[props.variant] ?? variants['muted']),
	props.clickable ? 'cursor-pointer transition hover:-translate-y-px hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300' : '',
	props.active ? '!border-emerald-600 !bg-emerald-600 !text-white shadow-sm' : '',
	props.disabled ? 'pointer-events-none cursor-not-allowed opacity-60' : '',
	attrs['class']
]);
function onClick(event: MouseEvent) {
	if (props.clickable && !props.disabled) emit('click', event);
}
</script>

<template>
	<button v-if="clickable" v-bind="attrs" type="button" :class="classes" :disabled="disabled" :title="title" :aria-pressed="active ? 'true' : 'false'" @click="onClick">
		<font-awesome-icon v-if="icon" :icon="icon" class="h-3 w-3" /><slot>{{ value }}</slot>
	</button>
	<span v-else v-bind="attrs" :class="classes" :title="title"
		><font-awesome-icon v-if="icon" :icon="icon" class="h-3 w-3" /><slot>{{ value }}</slot></span
	>
</template>
